// One player's Pick Three season: every entry up to `throughWeek`, and their
// placing for each of those weeks (pick_three_standing, counts only, withheld
// by the server until a week's last lock and ten players). Scoring happens in
// seasonSummary (games/pickThree.js) from the teams map the caller passes.
//
// Placings share usePickThreeStanding's cache keys, so a week already looked
// at costs nothing here.
import { useEffect, useState } from "react";
import { supabase } from "../supabase/supabase";
import { useAuth } from "../context/AuthContext";
import { getCached, setCached, IDLE_TTL_MS } from "../utils/queryCache";

export function usePickThreeSeason(throughWeek) {
  const { currentUser } = useAuth();
  const userId = currentUser?.id;
  const [entries, setEntries] = useState(null);   // { [week]: { teams, captain } }
  const [standings, setStandings] = useState({}); // { [week]: standing }

  useEffect(() => {
    if (throughWeek === null || !userId) return undefined;
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("pick_three_entries")
        .select("week, teams, captain")
        .eq("user_id", userId)
        .lte("week", throughWeek);
      if (cancelled || error) return;
      const byWeek = Object.fromEntries((data || []).map((r) => [r.week, { teams: r.teams, captain: r.captain }]));
      setEntries(byWeek);

      const weeks = Object.keys(byWeek).map(Number);
      const results = await Promise.all(weeks.map(async (week) => {
        const key = `pick3:standing:${week}:${userId}`;
        const cached = getCached(key);
        if (cached) return [week, cached.value];
        const { data: standing, error: rpcError } = await supabase.rpc("pick_three_standing", { p_week: week });
        if (rpcError) return [week, null];
        setCached(key, { value: standing }, IDLE_TTL_MS);
        return [week, standing];
      }));
      if (!cancelled) setStandings(Object.fromEntries(results));
    })();
    return () => { cancelled = true; };
  }, [throughWeek, userId]);

  return { entries, standings };
}
